import { useMemo, useState } from "react";
import { useModals } from "../components/common/ModalsProvider.js";
import { FiscalYearSelector } from "../components/fiscal/FiscalYearSelector.js";
import {
  MonthlyEntryGrid,
  type EntryRow,
  type MonthCell,
} from "../components/fiscal/MonthlyEntryGrid.js";
import {
  FiscalCard,
  FiscalShell,
  LoadingSkeleton,
  fiscalInputClass,
} from "../components/fiscal/Shell.js";
import { formatMoney } from "../lib/format-fr.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useFiscalSelection } from "../hooks/useFiscalSelection.js";
import { trpc } from "../lib/trpc.js";
import { useT } from "../i18n/index.js";

const REQUIRED_FIELDS = [
  {
    key: "fournituresConsommables",
    label: "fiscalEntries.fieldFournituresConsommables",
    group: "fiscalEntries.groupChargesExternes",
  },
  {
    key: "locationsChargesLocatives",
    label: "fiscalEntries.fieldLocationsChargesLocatives",
    group: "fiscalEntries.groupChargesExternes",
  },
  {
    key: "primesAssurance",
    label: "fiscalEntries.fieldPrimesAssurance",
    group: "fiscalEntries.groupChargesExternes",
  },
  {
    key: "intermediairesHonoraires",
    label: "fiscalEntries.fieldIntermediairesHonoraires",
    group: "fiscalEntries.groupChargesExternes",
  },
  {
    key: "deplacementsReception",
    label: "fiscalEntries.fieldDeplacementsReception",
    group: "fiscalEntries.groupChargesExternes",
  },
  {
    key: "fraisBancaires",
    label: "fiscalEntries.fieldFraisBancaires",
    group: "fiscalEntries.groupChargesExternes",
  },
  {
    key: "impotsTaxes",
    label: "fiscalEntries.fieldImpotsTaxes",
    group: "fiscalEntries.groupOutrasCargas",
  },
  {
    key: "chargesPersonnel",
    label: "fiscalEntries.fieldChargesPersonnel",
    group: "fiscalEntries.groupOutrasCargas",
  },
  {
    key: "dotationsAmortissements",
    label: "fiscalEntries.fieldDotationsAmortissements",
    group: "fiscalEntries.groupOutrasCargas",
  },
  {
    key: "dotationsProvisions",
    label: "fiscalEntries.fieldDotationsProvisions",
    group: "fiscalEntries.groupOutrasCargas",
  },
  {
    key: "autresChargesExploitation",
    label: "fiscalEntries.fieldAutresChargesExploitation",
    group: "fiscalEntries.groupOutrasCargas",
  },
  {
    key: "impotSocietes",
    label: "fiscalEntries.fieldImpotSocietes",
    group: "fiscalEntries.groupOutrasCargas",
  },
  {
    key: "subventionsExploitation",
    label: "fiscalEntries.fieldSubventionsExploitation",
    group: "fiscalEntries.groupOutrosProdutos",
  },
  {
    key: "autresProduitsExploitation",
    label: "fiscalEntries.fieldAutresProduitsExploitation",
    group: "fiscalEntries.groupOutrosProdutos",
  },
  // F-recettes-encaissees — lado das ENTRADAS do livre de caisse; preenchidas
  // pelo botão "Sincronizar do banco" (editáveis com a mesma semântica das
  // caixas de custo: o próximo sync sobrescreve, meses locked preservados).
  {
    key: "recettesEncaisseesHt",
    label: "fiscalEntries.fieldRecettesEncaisseesHt",
    group: "fiscalEntries.groupRecettes",
  },
  {
    key: "tvaCollecteeEncaissements",
    label: "fiscalEntries.fieldTvaCollecteeEncaissements",
    group: "fiscalEntries.groupRecettes",
  },
  {
    key: "recettesATrier",
    label: "fiscalEntries.fieldRecettesATrier",
    group: "fiscalEntries.groupRecettes",
  },
] as const;

const BILAN_FIELDS = [
  { key: "immobilisationsNettes", label: "fiscalEntries.fieldImmobilisationsNettes" },
  { key: "creancesClients", label: "fiscalEntries.fieldCreancesClients" },
  { key: "autresCreances", label: "fiscalEntries.fieldAutresCreances" },
  { key: "tresorerie", label: "fiscalEntries.fieldTresorerie" },
  { key: "capitauxPropres", label: "fiscalEntries.fieldCapitauxPropres" },
  { key: "dettesFinancieres", label: "fiscalEntries.fieldDettesFinancieres" },
  { key: "dettesFournisseurs", label: "fiscalEntries.fieldDettesFournisseurs" },
  { key: "autresDettes", label: "fiscalEntries.fieldAutresDettes" },
] as const;

type RequiredKey = (typeof REQUIRED_FIELDS)[number]["key"];
type BilanKey = (typeof BILAN_FIELDS)[number]["key"];

export default function FiscalEntries() {
  const { user } = useAuth();
  const { companyId, fiscalYearId, setCompanyId, setFiscalYearId } = useFiscalSelection();

  const fyQuery = trpc.fiscalYears.getById.useQuery(
    { companyId: companyId ?? 0, fiscalYearId: fiscalYearId ?? 0 },
    {
      enabled: companyId !== null && fiscalYearId !== null,
      staleTime: 0,
      refetchOnMount: "always",
    },
  );

  const utils = trpc.useUtils();

  // F-roles — viewer (somente leitura) nunca vê controles de escrita, mesmo que
  // a checagem por empresa abaixo passasse.
  const canWrite = useCanEdit();

  const isAdmin =
    canWrite &&
    (user?.isSuperAdmin ||
      user?.memberships.some((m) => m.companyId === companyId && m.active && m.role === "admin"));
  const canEdit =
    canWrite &&
    (user?.isSuperAdmin ||
      user?.memberships.some(
        (m) => m.companyId === companyId && m.active && (m.role === "admin" || m.role === "user"),
      ));

  const modals = useModals();
  const t = useT();
  const [modal, setModal] = useState<MonthCell | null>(null);
  const [newFyModal, setNewFyModal] = useState(false);

  const lockMutation = trpc.fiscalMonthEntries.lock.useMutation({
    onSuccess: () => {
      utils.fiscalYears.invalidate();
      utils.fiscalMonthEntries.invalidate();
    },
    onError: (e) =>
      void modals.alert({
        title: t("fiscalEntries.errorTitle"),
        message: `${t("fiscalEntries.lockFailPre")}: ${e.message}`,
        tone: "danger",
      }),
  });
  const unlockMutation = trpc.fiscalMonthEntries.unlock.useMutation({
    onSuccess: () => {
      utils.fiscalYears.invalidate();
      utils.fiscalMonthEntries.invalidate();
    },
    onError: (e) =>
      void modals.alert({
        title: t("fiscalEntries.errorTitle"),
        message: `${t("fiscalEntries.unlockFailPre")}: ${e.message}`,
        tone: "danger",
      }),
  });
  const closeMutation = trpc.fiscalYears.close.useMutation({
    onSuccess: () => utils.fiscalYears.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("fiscalEntries.errorTitle"),
        message: `${t("fiscalEntries.closeFyFailPre")}: ${e.message}`,
        tone: "danger",
      }),
  });
  const reopenMutation = trpc.fiscalYears.reopen.useMutation({
    onSuccess: () => utils.fiscalYears.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("fiscalEntries.errorTitle"),
        message: `${t("fiscalEntries.reopenFyFailPre")}: ${e.message}`,
        tone: "danger",
      }),
  });

  const syncMutation = trpc.fiscalMonthEntries.syncFromLedger.useMutation({
    onSuccess: (r) => {
      utils.fiscalMonthEntries.invalidate();
      utils.fiscalYears.invalidate();
      void modals.alert({
        title: t("fiscalEntries.syncSuccessTitle"),
        message:
          `${r.monthsUpdated} ${t("fiscalEntries.syncMonthsUpdatedSuf")}` +
          (r.monthsSkippedLocked > 0
            ? ` · ${r.monthsSkippedLocked} ${t("fiscalEntries.syncMonthsSkippedSuf")}`
            : "") +
          // F-recettes-encaissees — resumo do lado das entradas.
          ` · ${t("fiscalEntries.syncRecettesPre")} ${formatMoney(r.recetteTotals.ht, { decimals: 2 })}` +
          (r.recetteTotals.aTrier > 0
            ? ` · ${t("fiscalEntries.syncATrierPre")} ${formatMoney(r.recetteTotals.aTrier, { decimals: 2 })}`
            : "") +
          `. ${t("fiscalEntries.syncRecomputeHint")}`,
      });
    },
    onError: (e) =>
      void modals.alert({
        title: t("fiscalEntries.errorTitle"),
        message: `${t("fiscalEntries.syncFailPre")}: ${e.message}`,
        tone: "danger",
      }),
  });

  const fy = fyQuery.data;

  const toolbar = (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <FiscalYearSelector
        companyId={companyId}
        fiscalYearId={fiscalYearId}
        onChangeCompany={setCompanyId}
        onChangeFiscalYear={(fyOpt) => setFiscalYearId(fyOpt?.id ?? null)}
      />
      {companyId !== null && canEdit && (
        <div className="flex items-center gap-2">
          {fiscalYearId !== null && (
            <button
              onClick={() => syncMutation.mutate({ companyId, fiscalYearId })}
              disabled={syncMutation.isPending}
              title={t("fiscalEntries.syncBtnTitle")}
              className="rounded-md border border-sky-500/40 hover:border-sky-500 text-sky-300 px-3 py-1.5 text-xs disabled:opacity-50"
            >
              {syncMutation.isPending ? t("fiscalEntries.syncingBtn") : t("fiscalEntries.syncBtn")}
            </button>
          )}
          <button
            onClick={() => setNewFyModal(true)}
            className="rounded-md border border-lime/40 hover:border-lime text-lime px-3 py-1.5 text-xs"
          >
            {t("fiscalEntries.newFyBtn")}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <FiscalShell subtitle={t("fiscalEntries.subtitle")} toolbar={toolbar}>
      {companyId === null && (
        <FiscalCard>
          <p className="text-sm text-neutral-400">{t("fiscalEntries.selectCompany")}</p>
        </FiscalCard>
      )}

      {newFyModal && companyId !== null && (
        <NewFyModal
          companyId={companyId}
          onClose={() => setNewFyModal(false)}
          onCreated={(id) => {
            setFiscalYearId(id);
            setNewFyModal(false);
            utils.fiscalYears.invalidate();
          }}
        />
      )}

      {companyId !== null && fiscalYearId === null && (
        <FiscalCard title={t("fiscalEntries.selectFyTitle")}>
          <p className="text-sm text-neutral-400">{t("fiscalEntries.selectFyHint")}</p>
        </FiscalCard>
      )}

      {fyQuery.isLoading && fiscalYearId !== null && (
        <FiscalCard>
          <LoadingSkeleton rows={6} />
        </FiscalCard>
      )}

      {fy && companyId !== null && (
        <>
          <FiscalCard
            title={`${t("fiscalEntries.fiscalYearPre")} ${fy.label}`}
            subtitle={`${fy.startDate} → ${fy.endDate} · ${fy.nbJours} ${t("fiscalEntries.daysSuf")} · ${Number(fy.nbMois).toFixed(2)} ${t("fiscalEntries.monthsSuf")}`}
            action={
              <div className="flex items-center gap-2">
                <span
                  className={
                    fy.status === "open"
                      ? "rounded bg-lime/15 text-lime px-2 py-0.5 text-xs"
                      : fy.status === "closed"
                        ? "rounded bg-amber-500/15 text-amber-300 px-2 py-0.5 text-xs"
                        : "rounded bg-neutral-700/40 text-neutral-300 px-2 py-0.5 text-xs"
                  }
                >
                  {fy.status}
                </span>
                {isAdmin && fy.status === "open" && (
                  <button
                    disabled={closeMutation.isPending}
                    onClick={async () => {
                      const reason = await modals.promptReason({
                        title: t("fiscalEntries.closeFyModalTitle"),
                        message: t("fiscalEntries.closeFyModalMsg"),
                        placeholder: t("fiscalEntries.closeFyPlaceholder"),
                        confirmLabel: t("fiscalEntries.closeFyBtn"),
                        tone: "warning",
                        required: true,
                      });
                      if (!reason || !companyId) return;
                      closeMutation.mutate({
                        companyId,
                        fiscalYearId: fy.id,
                        reason,
                      });
                    }}
                    className="rounded-md border border-amber-500/40 hover:border-amber-500 disabled:opacity-50 text-amber-400 px-3 py-1 text-xs"
                  >
                    {closeMutation.isPending
                      ? t("fiscalEntries.closingFy")
                      : t("fiscalEntries.closeFyBtn")}
                  </button>
                )}
                {isAdmin && fy.status === "closed" && (
                  <button
                    disabled={reopenMutation.isPending}
                    onClick={async () => {
                      const reason = await modals.promptReason({
                        title: t("fiscalEntries.reopenFyModalTitle"),
                        message: t("fiscalEntries.reopenFyModalMsg"),
                        placeholder: t("fiscalEntries.reopenFyPlaceholder"),
                        confirmLabel: t("fiscalEntries.reopenConfirm"),
                        required: true,
                      });
                      if (!reason || !companyId) return;
                      reopenMutation.mutate({
                        companyId,
                        fiscalYearId: fy.id,
                        reason,
                      });
                    }}
                    className="rounded-md border border-lime/40 hover:border-lime disabled:opacity-50 text-lime px-3 py-1 text-xs"
                  >
                    {reopenMutation.isPending
                      ? t("fiscalEntries.reopeningFy")
                      : t("fiscalEntries.reopenFyBtn")}
                  </button>
                )}
              </div>
            }
            padded={false}
          >
            <MonthlyEntryGrid
              fyStartDate={fy.startDate}
              fyEndDate={fy.endDate}
              fyStatus={fy.status}
              entries={fy.entries as EntryRow[]}
              isAdmin={!!isAdmin}
              canEdit={!!canEdit}
              onEdit={(slot) => setModal(slot)}
              onLock={async (entry) => {
                if (!companyId) return;
                const reason = await modals.promptReason({
                  title: t("fiscalEntries.lockEntryTitle"),
                  message: t("fiscalEntries.lockEntryMsg"),
                  placeholder: t("fiscalEntries.lockEntryPlaceholder"),
                  required: false,
                });
                if (reason === null) return; // cancelado
                lockMutation.mutate(
                  reason
                    ? { companyId, entryId: entry.id, reason }
                    : { companyId, entryId: entry.id },
                );
              }}
              onUnlock={async (entry) => {
                if (!companyId) return;
                const reason = await modals.promptReason({
                  title: t("fiscalEntries.unlockEntryTitle"),
                  message: t("fiscalEntries.unlockEntryMsg"),
                  placeholder: t("fiscalEntries.unlockEntryPlaceholder"),
                  required: true,
                });
                if (!reason) return;
                unlockMutation.mutate({
                  companyId,
                  entryId: entry.id,
                  reason,
                });
              }}
            />
          </FiscalCard>
        </>
      )}

      {modal && companyId !== null && fy && (
        <EntryModal
          companyId={companyId}
          fiscalYearId={fy.id}
          slot={modal}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            utils.fiscalYears.invalidate();
            utils.fiscalMonthEntries.invalidate();
          }}
        />
      )}
    </FiscalShell>
  );
}

function NewFyModal({
  companyId,
  onClose,
  onCreated,
}: {
  companyId: number;
  onClose: () => void;
  onCreated: (id: number) => void;
}) {
  const [label, setLabel] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [error, setError] = useState<string | null>(null);
  const t = useT();

  const mut = trpc.fiscalYears.create.useMutation({
    onSuccess: (row) => onCreated(row.id),
    onError: (e) => setError(e.message),
  });

  return (
    <ModalShell title={t("fiscalEntries.newFyModalTitle")} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          mut.mutate({
            companyId,
            label: label.trim(),
            startDate,
            endDate,
          });
        }}
        className="p-5 space-y-4"
      >
        <Field label={t("fiscalEntries.labelField")} required>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            maxLength={32}
            placeholder="2027"
            className={fiscalInputClass + " w-full"}
            disabled={mut.isPending}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("fiscalEntries.startField")} required>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
              className={fiscalInputClass + " w-full"}
              disabled={mut.isPending}
            />
          </Field>
          <Field label={t("fiscalEntries.endField")} required>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              required
              className={fiscalInputClass + " w-full"}
              disabled={mut.isPending}
            />
          </Field>
        </div>
        {error && (
          <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2 border-t border-neutral-800">
          <button
            type="button"
            onClick={onClose}
            disabled={mut.isPending}
            className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
          >
            {t("fiscalEntries.cancel")}
          </button>
          <button
            type="submit"
            disabled={mut.isPending}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
          >
            {mut.isPending ? t("fiscalEntries.creatingFy") : t("fiscalEntries.createFyBtn")}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

type FormState = {
  required: Record<RequiredKey, string>;
  bilan: Record<BilanKey, string>;
  notes: string;
};

function buildEmptyForm(entry: EntryRow | null): FormState {
  const req = {} as Record<RequiredKey, string>;
  for (const f of REQUIRED_FIELDS) {
    req[f.key] = entry ? String(Number(entry[f.key])) : "0";
  }
  const bilan = {} as Record<BilanKey, string>;
  for (const f of BILAN_FIELDS) {
    bilan[f.key] = entry && entry[f.key] !== null ? String(Number(entry[f.key])) : "";
  }
  return { required: req, bilan, notes: "" };
}

function EntryModal({
  companyId,
  fiscalYearId,
  slot,
  onClose,
  onSaved,
}: {
  companyId: number;
  fiscalYearId: number;
  slot: MonthCell;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<FormState>(() => buildEmptyForm(slot.entry));
  const [error, setError] = useState<string | null>(null);
  const t = useT();
  const upsert = trpc.fiscalMonthEntries.upsert.useMutation({
    onSuccess: onSaved,
    onError: (e) => setError(e.message),
  });

  const grouped = useMemo(() => {
    const out = new Map<(typeof REQUIRED_FIELDS)[number]["group"], typeof REQUIRED_FIELDS>();
    for (const f of REQUIRED_FIELDS) {
      const arr = (out.get(f.group) ?? []) as Array<(typeof REQUIRED_FIELDS)[number]>;
      arr.push(f);
      out.set(f.group, arr as unknown as typeof REQUIRED_FIELDS);
    }
    return out;
  }, []);

  function parseRequired(s: string): number | undefined {
    if (!s.trim()) return 0;
    const n = Number(s.replace(",", "."));
    return Number.isFinite(n) ? n : undefined;
  }
  function parseBilan(s: string): number | null | undefined {
    if (!s.trim()) return null;
    const n = Number(s.replace(",", "."));
    return Number.isFinite(n) ? n : undefined;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const patch: Record<string, number | null> = {};
    for (const f of REQUIRED_FIELDS) {
      const v = parseRequired(form.required[f.key]);
      if (v === undefined) {
        setError(`${t("fiscalEntries.invalidValuePre")} ${t(f.label)}.`);
        return;
      }
      patch[f.key] = v;
    }
    for (const f of BILAN_FIELDS) {
      const v = parseBilan(form.bilan[f.key]);
      if (v === undefined) {
        setError(`${t("fiscalEntries.invalidValuePre")} ${t(f.label)}.`);
        return;
      }
      patch[f.key] = v;
    }
    upsert.mutate({
      companyId,
      fiscalYearId,
      month: slot.month,
      year: slot.year,
      patch,
    });
  }

  const monthName = new Intl.DateTimeFormat("pt-BR", { month: "long" }).format(
    new Date(slot.year, slot.month - 1, 1),
  );

  return (
    <ModalShell
      title={`${slot.entry ? t("fiscalEntries.editEntryTitle") : t("fiscalEntries.createEntryTitle")} — ${monthName}/${slot.year}`}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="p-5 space-y-5">
        {[...grouped.entries()].map(([group, fields]) => (
          <div key={group}>
            <h3 className="text-xs uppercase tracking-wider text-neutral-500 mb-2">{t(group)}</h3>
            <div className="grid grid-cols-2 gap-3">
              {fields.map((f) => (
                <Field key={f.key} label={t(f.label)}>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={form.required[f.key]}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        required: { ...prev.required, [f.key]: e.target.value },
                      }))
                    }
                    className={fiscalInputClass + " w-full font-mono text-right"}
                    disabled={upsert.isPending}
                  />
                </Field>
              ))}
            </div>
          </div>
        ))}

        <div>
          <h3 className="text-xs uppercase tracking-wider text-neutral-500 mb-2">
            {t("fiscalEntries.bilanSectionTitle")}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {BILAN_FIELDS.map((f) => (
              <Field key={f.key} label={t(f.label)}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.bilan[f.key]}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      bilan: { ...prev.bilan, [f.key]: e.target.value },
                    }))
                  }
                  placeholder="—"
                  className={fiscalInputClass + " w-full font-mono text-right"}
                  disabled={upsert.isPending}
                />
              </Field>
            ))}
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-3 border-t border-neutral-800 sticky bottom-0 bg-neutral-950">
          <button
            type="button"
            onClick={onClose}
            disabled={upsert.isPending}
            className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
          >
            {t("fiscalEntries.cancel")}
          </button>
          <button
            type="submit"
            disabled={upsert.isPending}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
          >
            {upsert.isPending ? t("fiscalEntries.saving") : t("fiscalEntries.save")}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs text-neutral-400 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
    </div>
  );
}

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-3xl bg-neutral-950 border border-neutral-800 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-neutral-800 flex items-center justify-between sticky top-0 bg-neutral-950">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-300 text-lg leading-none"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
